import {
  rateCodeCategoryKeyByLabel,
  rateCodeCategoryNames
} from 'features/RateCodes/rateCodeCategories'

const HEADER_SCAN_ROW_LIMIT = 10
const HEADER_SCAN_COLUMN_LIMIT = 20

const CODE_HEADER = 'value'
const DESCRIPTION_HEADER = 'description'
const MAPPING_HEADER = 'mapping'

const HOTEL_NAME_MINIMUM_TOKEN_LENGTH = 3
const HOTEL_NAME_STOP_WORDS = [
  'inn',
  'hotel',
  'hotels',
  'suite',
  'suites',
  'resort',
  'the',
  'and',
  'by',
  'of'
]

/**
 * Lighthouse "Segment > Category" value → Directful category label.
 * Values pointing at Uncategorized are recognized on purpose: Lighthouse cannot express
 * the distinction we need (for example group no reach versus group no convert), so those
 * rows are handed back to a human instead of being guessed.
 */
export const LIGHTHOUSE_MAPPING_TO_CATEGORY = {
  'transient > corp negotiated': rateCodeCategoryNames.CorporateBrand,
  'transient > local negotiated': rateCodeCategoryNames.CorporateLocal,
  'transient > ota discount': rateCodeCategoryNames.OTA,
  'transient > ota retail': rateCodeCategoryNames.OTA,
  'transient > brand discount': rateCodeCategoryNames.DiscountBrand,
  'transient > corp discount': rateCodeCategoryNames.DiscountBrand,
  'transient > retail': rateCodeCategoryNames.Generic,
  'transient > qualified discount': rateCodeCategoryNames.DiscountClub,
  'transient > opaque': rateCodeCategoryNames.OTAOpaque,
  'other > employee': rateCodeCategoryNames.Employee,
  'other > redemption': rateCodeCategoryNames.RewardPointsClaim,
  group: rateCodeCategoryNames.Unknown,
  'group > group': rateCodeCategoryNames.Unknown,
  'transient > government': rateCodeCategoryNames.Unknown,
  'transient > packages': rateCodeCategoryNames.Unknown,
  transient: rateCodeCategoryNames.Unknown,
  other: rateCodeCategoryNames.Unknown,
  unmapped: rateCodeCategoryNames.Unknown
}

/**
 * ExcelJS cell values are not always strings: shared formulas, rich text and hyperlinks
 * arrive as objects.
 */
export function readCellText(value) {
  if (value === null || value === undefined) {
    return ''
  }

  if (value instanceof Date) {
    return value.toISOString()
  }

  if (typeof value === 'object') {
    if (Array.isArray(value.richText)) {
      return value.richText.map(part => part.text).join('')
    }
    if (value.text !== undefined) {
      return String(value.text)
    }
    if (value.result !== undefined) {
      return String(value.result)
    }
    return ''
  }

  return String(value)
}

function readCell(row, column) {
  if (!column) {
    return ''
  }
  return readCellText(row.getCell(column).value).trim()
}

export function normalizeMappingValue(value) {
  return readCellText(value)
    .replace(/\s*>\s*/g, ' > ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

/**
 * Resolves a Lighthouse mapping value.
 * isRecognized false means Lighthouse used a value we have never seen and the table needs
 * updating. isUncategorized true means we know the value and deliberately leave it to a human.
 */
export function mapLighthouseMapping(mappingValue) {
  const normalized = normalizeMappingValue(mappingValue)
  const categoryLabel = LIGHTHOUSE_MAPPING_TO_CATEGORY[normalized]

  if (!categoryLabel) {
    return { isRecognized: false, isUncategorized: true, categoryLabel: null, categoryKey: null }
  }

  const categoryKey = rateCodeCategoryKeyByLabel[categoryLabel]

  return {
    isRecognized: true,
    isUncategorized: categoryKey === 'Unknown',
    categoryLabel,
    categoryKey
  }
}

function normalizeHeaderText(value) {
  return readCellText(value).replace(/\s+/g, ' ').trim().toLowerCase()
}

function findHeaderColumns(row) {
  const columnsByHeader = {}

  for (let column = 1; column <= HEADER_SCAN_COLUMN_LIMIT; column += 1) {
    const header = normalizeHeaderText(row.getCell(column).value)

    if (!columnsByHeader[header]) {
      columnsByHeader[header] = column
    }
  }

  const codeCol = columnsByHeader[CODE_HEADER]
  const mappingCol = columnsByHeader[MAPPING_HEADER]

  if (!codeCol || !mappingCol) {
    return null
  }

  return {
    codeCol,
    descriptionCol: columnsByHeader[DESCRIPTION_HEADER] || null,
    mappingCol
  }
}

/**
 * Finds the Lighthouse header row. The header is not on row 1 and the data does not start
 * on column A, so both are discovered rather than assumed.
 */
export function detectLighthouseSheet(workbook) {
  if (!workbook || !workbook.worksheets) {
    return null
  }

  for (const sheet of workbook.worksheets) {
    const lastRow = Math.min(sheet.rowCount || 0, HEADER_SCAN_ROW_LIMIT)

    for (let rowNumber = 1; rowNumber <= lastRow; rowNumber += 1) {
      const columns = findHeaderColumns(sheet.getRow(rowNumber))

      if (columns) {
        return { sheet, headerRow: rowNumber, ...columns }
      }
    }
  }

  return null
}

/**
 * Reads the hotel name Lighthouse writes above the header row, used to warn when the file
 * does not belong to the hotel currently selected in the dashboard.
 */
export function readHotelNameFromSheet(sheet, headerRow) {
  for (let rowNumber = 1; rowNumber < headerRow; rowNumber += 1) {
    const row = sheet.getRow(rowNumber)

    for (let column = 1; column <= HEADER_SCAN_COLUMN_LIMIT; column += 1) {
      const text = readCell(row, column)

      if (text) {
        return text
      }
    }
  }

  return ''
}

function toHotelNameTokens(hotelName) {
  return readCellText(hotelName)
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(
      token =>
        token.length >= HOTEL_NAME_MINIMUM_TOKEN_LENGTH &&
        !HOTEL_NAME_STOP_WORDS.includes(token)
    )
}

/**
 * Lighthouse writes the property name its own way ("HAMPTON WENTZVILLE" against our
 * "Hampton Inn Wentzville"), so relatedness is judged on shared distinctive words rather
 * than equality. Returns true when we cannot tell, to keep the warning meaningful.
 */
export function hotelNamesLookRelated(hotelNameFromFile, selectedHotelName) {
  const fileTokens = toHotelNameTokens(hotelNameFromFile)
  const selectedTokens = toHotelNameTokens(selectedHotelName)

  if (fileTokens.length === 0 || selectedTokens.length === 0) {
    return true
  }

  return fileTokens.some(token => selectedTokens.includes(token))
}

/**
 * Reads a detected Lighthouse sheet into the same bucket shape the native import produces.
 * Output:
 * {
 *   buckets: { OTA: [{code}], ... },
 *   uncategorizedRows: [{ code, description, category, rowNumber }],
 *   unknownRows: [{ code, description, category, rowNumber }],
 *   hotelNameFromFile: string,
 *   rowCount: number
 * }
 */
export function parseLighthouseSheet(detection) {
  const { sheet, headerRow, codeCol, descriptionCol, mappingCol } = detection

  const buckets = {}

  Object.keys(rateCodeCategoryNames).forEach(key => {
    if (key !== 'Unknown') {
      buckets[key] = []
    }
  })

  const uncategorizedRows = []
  const unknownRows = []
  let rowCount = 0

  // Last row wins for duplicate codes (case-insensitive), matching the native import and
  // the backend save behaviour.
  const codeAssignments = new Map()

  sheet.eachRow((row, rowNumber) => {
    if (rowNumber <= headerRow) return

    const code = readCell(row, codeCol)

    if (!code) return

    rowCount += 1

    const description = readCell(row, descriptionCol)
    const mapping = readCell(row, mappingCol)
    const mapped = mapLighthouseMapping(mapping)

    if (!mapped.isRecognized || mapped.isUncategorized) {
      codeAssignments.delete(code.toLowerCase())

      const reportedRow = {
        code,
        description,
        category: mapping || '(empty)',
        rowNumber
      }

      if (mapped.isRecognized) {
        uncategorizedRows.push(reportedRow)
      } else {
        unknownRows.push(reportedRow)
      }
      return
    }

    codeAssignments.set(code.toLowerCase(), { bucketKey: mapped.categoryKey, code })
  })

  codeAssignments.forEach(({ bucketKey, code }) => {
    buckets[bucketKey].push({ code })
  })

  return {
    buckets,
    uncategorizedRows,
    unknownRows,
    hotelNameFromFile: readHotelNameFromSheet(sheet, headerRow),
    rowCount
  }
}

export default parseLighthouseSheet
